import type { CNSWorkflowSnapshot } from "@/models/cns";
import { resolveWorkflowLearningPublicId } from "@/lib/workflowLearningIdentity";

export type ArtifactTabId = "overview" | "data-tracker" | "insights" | "learning";

export const ARTIFACT_TAB_ORDER: readonly ArtifactTabId[] = [
  "overview",
  "data-tracker",
  "insights",
  "learning",
] as const;

export function isArtifactTabId(value: string): value is ArtifactTabId {
  return (ARTIFACT_TAB_ORDER as readonly string[]).includes(value);
}

export const ARTIFACT_TAB_LABELS: Record<ArtifactTabId, string> = {
  overview: "Overview",
  "data-tracker": "Data Tracker",
  insights: "Insights",
  learning: "Learning",
};

export type LearningTabState =
  | "locked"
  | "gate-blocked"
  | "qa-in-progress"
  | "complete";

const LEARNING_ACTION_TYPES: ReadonlySet<string> = new Set([
  "learning_gate",
  "learning_qa",
  "learning_card",
]);

function hasAction(snapshot: CNSWorkflowSnapshot, actionType: string): boolean {
  return snapshot.activeActionRequests.some((request) => request.actionType === actionType);
}

function isLearningFlowActive(snapshot: CNSWorkflowSnapshot): boolean {
  if (snapshot.currentStage === "LEARNING") {
    return true;
  }
  if (snapshot.activeActionRequests.some((request) => LEARNING_ACTION_TYPES.has(request.actionType))) {
    return true;
  }
  return false;
}

function isLearningTabUnlocked(snapshot: CNSWorkflowSnapshot | undefined): boolean {
  if (!snapshot?.primaryExperimentPublicId) {
    return false;
  }
  return isLearningFlowActive(snapshot);
}

/**
 * Whether the Data Tracker tab should be reachable for the focused experiment.
 * Unlocks during {@code SETUP_TRACKER} (and when {@code setup_tracker} is an eligible catalog
 * kind) so live header patches are visible before {@code trackerPresent} flips true.
 */
export function isDataTrackerTabUnlocked(
  snapshot: CNSWorkflowSnapshot | undefined,
): boolean {
  if (!snapshot?.primaryExperimentPublicId) {
    return false;
  }
  if (snapshot.trackerPresent === true) {
    return true;
  }
  return hasAction(snapshot, "setup_tracker");
}

/**
 * Snapshot-driven unlock map for Workbench tabs. Overview is always available once a snapshot
 * is loaded so discovery-phase lineage renders in the unified accumulating Overview.
 */
export function getArtifactTabUnlockMap(
  snapshot: CNSWorkflowSnapshot | undefined,
): Record<ArtifactTabId, boolean> {
  const hasSnapshot = Boolean(snapshot);
  const hasExperiment = Boolean(snapshot?.primaryExperimentPublicId);

  return {
    overview: hasSnapshot,
    "data-tracker": isDataTrackerTabUnlocked(snapshot),
    insights:
      hasExperiment &&
      snapshot?.experimentStatus === "COMPLETED" &&
      snapshot?.trackerLocked === true,
    learning: isLearningTabUnlocked(snapshot),
  };
}

/**
 * Learning tab body substate for gate-blocked, Q&A, and validated card views.
 */
export function getLearningTabState(
  snapshot: CNSWorkflowSnapshot | undefined,
): LearningTabState {
  if (!isLearningTabUnlocked(snapshot) || !snapshot) {
    return "locked";
  }

  const { currentStage } = snapshot;

  if (hasAction(snapshot, "learning_gate")) {
    return "gate-blocked";
  }

  if (
    hasAction(snapshot, "learning_qa") ||
    hasAction(snapshot, "learning_card")
  ) {
    return "qa-in-progress";
  }

  if (
    currentStage === "LEARNING" &&
    snapshot.activeActionRequests.length === 0 &&
    snapshot.permittedTransitionIds.length === 0 &&
    resolveWorkflowLearningPublicId(snapshot)
  ) {
    return "complete";
  }

  if (currentStage === "LEARNING") {
    return "qa-in-progress";
  }

  return "gate-blocked";
}
